/**
 * Configuration Provider Component
 *
 * This component loads runtime configuration from the backend before
 * rendering the application. It shows a loading state while fetching
 * and handles errors gracefully.
 *
 * Usage:
 *   <ConfigProvider>
 *     <App />
 *   </ConfigProvider>
 */

import React, { useState, useEffect, createContext, useContext } from 'react';
import { Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import runtimeConfigService, { EnvironmentConfig } from '../config/runtimeConfig';
import { initDatadogRum } from '../config/datadogRum';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    width: '100vw',
    backgroundColor: tokens.colorNeutralBackground1,
    gap: tokens.spacingVerticalXL,
  },
  logo: {
    fontSize: '48px',
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorBrandForeground1,
  },
  message: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground2,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
    maxWidth: '600px',
    textAlign: 'center',
    padding: tokens.spacingHorizontalL,
  },
  retryButton: {
    marginTop: tokens.spacingVerticalL,
  },
});

interface ConfigContextValue {
  config: EnvironmentConfig | null;
  isLoading: boolean;
  error: Error | null;
}

const ConfigContext = createContext<ConfigContextValue>({
  config: null,
  isLoading: true,
  error: null,
});

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context.config && !context.isLoading) {
    throw new Error('Configuration not available');
  }
  return context;
};

interface ConfigProviderProps {
  children: React.ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const styles = useStyles();
  const [config, setConfig] = useState<EnvironmentConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load runtime configuration from backend
      const runtimeConfig = await runtimeConfigService.loadConfig();
      initDatadogRum(runtimeConfig);

      // Convert to EnvironmentConfig format
      const envConfig = runtimeConfigService.toEnvironmentConfig();
      setConfig(envConfig);

      console.log('✅ Configuration loaded and ready');
    } catch (err) {
      console.error('❌ Failed to load configuration:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  if (isLoading) {
    return (
      <div className={styles.container} data-testid="config-provider-loading">
        <div className={styles.logo} data-testid="config-provider-logo">LSEG Domain Admin</div>
        <Spinner size="extra-large" label="Loading configuration..." data-testid="config-provider-loading-spinner" />
        <Text className={styles.message} data-testid="config-provider-loading-message">Fetching runtime settings from backend...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container} data-testid="config-provider-error">
        <div className={styles.logo} data-testid="config-provider-logo-error">LSEG Domain Admin</div>
        <Text className={styles.error} data-testid="config-provider-error-message">
          Failed to load application configuration: {error.message}
        </Text>
        <Text className={styles.message} data-testid="config-provider-error-help-text">
          Please ensure the backend server is running and accessible.
        </Text>
        <button
          className={styles.retryButton}
          onClick={loadConfig}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            backgroundColor: tokens.colorBrandBackground,
            color: tokens.colorNeutralForegroundOnBrand,
            border: 'none',
            borderRadius: tokens.borderRadiusMedium,
            cursor: 'pointer',
          }} data-testid="config-provider-retry-button"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <ConfigContext.Provider value={{ config, isLoading, error }}>
      {children}
    </ConfigContext.Provider>
  );
};

export default ConfigProvider;
